import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { NameCard } from "@/components/NameCard";
import { NameDetailDialog } from "@/components/NameDetailDialog";
import { engineLabel } from "@/components/NamingEngineSelector";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useSaved } from "@/context/SavedContext";
import { useModals } from "@/context/ModalContext";
import { apiGetSearch } from "@/lib/namable";
import { Sparkles, ArrowLeft, RefreshCw, TrendingUp } from "lucide-react";

const SORTS = [
  { key: "recommended", label: "Recommended" },
  { key: "value", label: "Top value" },
  { key: "brand", label: "Best brand" },
  { key: "short", label: "Shortest" },
];

export default function Results() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { usage } = useAuth();
  const { isSaved, toggleSave } = useSaved();
  const { openPaywall } = useModals();

  const [search, setSearch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("recommended");
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [engineFilter, setEngineFilter] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let active = true;
    const cached = sessionStorage.getItem(`search:${id}`);
    if (cached) {
      try {
        setSearch(JSON.parse(cached));
        setLoading(false);
      } catch (e) {
        /* ignore */
      }
    }
    apiGetSearch(id)
      .then((d) => {
        if (active) {
          const meta = cached ? JSON.parse(cached)._meta : undefined;
          setSearch({ ...d, _meta: meta });
        }
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    window.scrollTo(0, 0);
    return () => {
      active = false;
    };
  }, [id]);

  const results = useMemo(() => search?.results || [], [search]);
  const availableCount = results.filter((r) => r.available === true).length;

  const engineCounts = useMemo(() => {
    const map = new Map();
    results.forEach((r) => {
      const key = r.strategy || "other";
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([key, count]) => ({ key, count, label: engineLabel(key) }))
      .sort((a, b) => b.count - a.count);
  }, [results]);

  const sorted = useMemo(() => {
    let list = [...results];
    if (onlyAvailable) list = list.filter((r) => r.available === true);
    if (engineFilter) list = list.filter((r) => (r.strategy || "other") === engineFilter);
    switch (sort) {
      case "value":
        list.sort((a, b) => (b.est_value_high || 0) - (a.est_value_high || 0) || (b.score || 0) - (a.score || 0));
        break;
      case "brand":
        list.sort((a, b) => (b.brand_score || 0) - (a.brand_score || 0));
        break;
      case "short":
        list.sort((a, b) => a.name.length - b.name.length);
        break;
      default:
        // available first, then score
        list.sort((a, b) => (b.available === true) - (a.available === true) || (b.score || 0) - (a.score || 0));
    }
    return list;
  }, [results, sort, onlyAvailable, engineFilter]);

  if (loading && !search) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-brand border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!search) {
    return (
      <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-20 text-center">
        <p className="text-ink-muted">We couldn't find that search.</p>
        <Button className="mt-4 bg-brand text-white rounded-lg" onClick={() => navigate("/generate")}>
          Start a new search
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <Seo
        title={`Name ideas for "${(search.description || "").slice(0, 40)}" — Namable`}
        description="AI-generated brandable name ideas with .com availability and estimated domain value."
        path={`/results/${id}`}
      />

      <Link to="/generate" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink-muted hover:text-ink transition-colors mb-4" data-testid="back-to-generator">
        <ArrowLeft size={15} /> New search
      </Link>

      <div className="card-soft p-5 sm:p-6">
        <p className="overline text-brand mb-1.5">Your idea</p>
        <p className="text-[15px] text-ink font-medium leading-relaxed">{search.description}</p>
        <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t hairline">
          <div>
            <p className="text-[22px] font-extrabold text-ink leading-none">{results.length}</p>
            <p className="text-[12px] text-ink-subtle mt-0.5">names</p>
          </div>
          <div>
            <p className="text-[22px] font-extrabold text-success leading-none">{availableCount}</p>
            <p className="text-[12px] text-ink-subtle mt-0.5">available</p>
          </div>
          <span className="inline-flex items-center rounded-full bg-canvas-alt px-2.5 py-1 text-[12px] font-semibold text-ink-muted">
            {search.engine_mode === "recommended"
              ? "Recommended"
              : `${(search.engines || []).length} engine${(search.engines || []).length === 1 ? "" : "s"}`}{" "}
            · {search.extension}
          </span>
        </div>
      </div>

      {/* controls */}
      <div className="flex items-center justify-between gap-3 mt-6 mb-4 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          {SORTS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSort(s.key)}
              data-testid={`sort-${s.key}`}
              className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${
                sort === s.key ? "bg-brand text-white" : "bg-surface border hairline text-ink-muted hover:text-ink"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-[13px] font-semibold text-ink-muted cursor-pointer select-none" data-testid="only-available-toggle">
          <input
            type="checkbox"
            checked={onlyAvailable}
            onChange={(e) => setOnlyAvailable(e.target.checked)}
            className="accent-brand w-4 h-4"
          />
          Available only
        </label>
      </div>

      {/* value banner for free users */}
      {usage && !usage.allow_com && (
        <button
          onClick={() => openPaywall("com_locked")}
          className="w-full mb-5 rounded-xl bg-brand-soft border border-brand/15 px-4 py-3.5 flex items-center justify-between text-left hover:bg-brand-soft/70 transition-colors"
          data-testid="unlock-com-banner"
        >
          <span className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-full brand-gradient flex items-center justify-center text-white">
              <TrendingUp size={17} />
            </span>
            <span>
              <span className="block text-[14px] font-bold text-ink">Unlock .com search & domain values</span>
              <span className="block text-[12px] text-ink-muted">See available .com domains and their estimated worth.</span>
            </span>
          </span>
          <span className="text-[13px] font-bold text-brand shrink-0">Upgrade</span>
        </button>
      )}

      {/* per-engine count chips */}
      {engineCounts.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="engine-count-chips">
          <button
            onClick={() => setEngineFilter(null)}
            data-testid="engine-chip-all"
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold border transition-colors ${
              engineFilter === null ? "bg-brand text-white border-brand" : "bg-surface text-ink-muted border-[color:var(--hairline-strong)] hover:text-ink"
            }`}
          >
            All <span className={engineFilter === null ? "text-white/80" : "text-ink-subtle"}>{results.length}</span>
          </button>
          {engineCounts.map((e) => {
            const active = engineFilter === e.key;
            return (
              <button
                key={e.key}
                onClick={() => setEngineFilter(active ? null : e.key)}
                data-testid={`engine-chip-${e.key}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold border transition-colors ${
                  active ? "bg-brand text-white border-brand" : "bg-surface text-ink-muted border-[color:var(--hairline-strong)] hover:text-ink"
                }`}
              >
                {e.label}
                <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-bold ${active ? "bg-white/25 text-white" : "bg-canvas-alt text-ink"}`}>
                  {e.count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {sorted.map((r, i) => (
          <NameCard
            key={r.name}
            result={{ ...r, search_id: id }}
            index={i}
            saved={isSaved(r.name)}
            onSave={toggleSave}
            onDetails={setDetail}
          />
        ))}
      </div>

      {sorted.length === 0 && (
        <div className="text-center py-12">
          <p className="text-ink-muted">No names match this view.</p>
          {(engineFilter || onlyAvailable) && (
            <button
              onClick={() => {
                setEngineFilter(null);
                setOnlyAvailable(false);
              }}
              className="mt-2 text-[13px] font-semibold text-brand hover:underline"
              data-testid="clear-filters-btn"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
        <Button
          onClick={() =>
            navigate(
              `/generate?desc=${encodeURIComponent(search.description || "")}&engines=${encodeURIComponent((search.engines || []).join(","))}&ext=${encodeURIComponent(search.extension || ".net")}&auto=1`
            )
          }
          className="h-12 rounded-xl bg-brand hover:bg-brand-dark text-white font-bold btn-glow"
          data-testid="rescan-btn"
        >
          <RefreshCw size={16} className="mr-2" /> Rescan for fresh names
        </Button>
        <Button variant="outline" onClick={() => navigate("/generate")} className="h-12 rounded-xl font-bold border-[color:var(--hairline-strong)]" data-testid="new-search-btn">
          <Sparkles size={16} className="mr-2" /> New idea
        </Button>
      </div>

      <p className="text-center text-[12px] text-ink-subtle mt-6 max-w-[420px] mx-auto">
        Domain values are AI estimates for guidance only — not professional appraisals. Always verify availability with a registrar before purchase.
      </p>

      <NameDetailDialog result={detail} description={search.description} onClose={() => setDetail(null)} />
    </div>
  );
}
